// One row of the activity feed, in words.
//
// The board already shows what an order IS. This says what HAPPENED to it, which
// the socket could never answer on its own: `order-status-change` carries the
// whole order for sixteen different actions and no verb at all, which is why the
// backend logs these separately (see util/order-log.js).
//
// Every sentence is built from the stored row plus, where it helps, the live
// lists the page already holds — a rider who was renamed since should read as
// their current name, not the one frozen into the log.

// Mirrors ORDER_EVENT in the backend's util/order-log.js.
export const ACTIVITY_EVENT = {
	CREATED: 'created',
	READY: 'ready',
	DELAY_CHANGED: 'delay-changed',
	EDITED: 'edited',
	COMPLETED: 'completed',
	CANCELLED: 'cancelled',
	ASSIGNED: 'assigned',
	UNASSIGNED: 'unassigned',
	REVOKED: 'revoked',
	CONFIRMED: 'confirmed',
	COLLECTED: 'collected',
	NOTIFIED_POOL: 'notified-pool',
	NOTIFIED_RIDER: 'notified-rider',
	// SWEEP_PULLED: 'sweep-pulled',   // parked — see util/order-log.js
	DELETED: 'deleted',
	CLOCK_IN: 'clock-in',
	CLOCK_OUT: 'clock-out',
	BREAK_IN: 'break-in',
	BREAK_OUT: 'break-out',
};

// The four questions someone scanning this is actually asking, which is what the
// filter offers and what the colours separate.
export const ACTIVITY_GROUPS = [
	{
		key: 'assignment',
		label: 'Αναθέσεις',
		types: [ACTIVITY_EVENT.ASSIGNED, ACTIVITY_EVENT.UNASSIGNED, ACTIVITY_EVENT.REVOKED],
	},
	{
		key: 'rider',
		label: 'Διανομείς',
		types: [
			ACTIVITY_EVENT.CONFIRMED,
			ACTIVITY_EVENT.COLLECTED,
			ACTIVITY_EVENT.CLOCK_IN,
			ACTIVITY_EVENT.CLOCK_OUT,
			ACTIVITY_EVENT.BREAK_IN,
			ACTIVITY_EVENT.BREAK_OUT,
		],
	},
	{
		key: 'order',
		label: 'Παραγγελίες',
		types: [
			ACTIVITY_EVENT.CREATED,
			ACTIVITY_EVENT.READY,
			ACTIVITY_EVENT.DELAY_CHANGED,
			ACTIVITY_EVENT.EDITED,
			ACTIVITY_EVENT.COMPLETED,
			ACTIVITY_EVENT.CANCELLED,
			ACTIVITY_EVENT.DELETED,
		],
	},
	{
		key: 'push',
		label: 'Ειδοποιήσεις',
		types: [ACTIVITY_EVENT.NOTIFIED_POOL, ACTIVITY_EVENT.NOTIFIED_RIDER],
	},
	// Parked with the event itself. Kept because the check moving is neither an
	// assignment nor a rider action, so when it comes back it wants its own
	// filter rather than being folded into one of the four above.
	// {
	// 	key: 'system',
	// 	label: 'Έλεγχος',
	// 	types: [ACTIVITY_EVENT.SWEEP_PULLED],
	// },
];

const GROUP_OF = Object.fromEntries(ACTIVITY_GROUPS.flatMap(g => g.types.map(t => [t, g.key])));

export const groupOfEvent = type => GROUP_OF[type] || 'order';

// Tone is per EVENT, not per group: within one group an assignment and a revoke
// mean opposite things, and a feed that coloured them alike would be decoration.
const TONES = {
	[ACTIVITY_EVENT.ASSIGNED]: 'teal',
	[ACTIVITY_EVENT.CONFIRMED]: 'violet',
	[ACTIVITY_EVENT.COLLECTED]: 'violet',
	[ACTIVITY_EVENT.COMPLETED]: 'green',
	[ACTIVITY_EVENT.CREATED]: 'slate',
	[ACTIVITY_EVENT.READY]: 'sky',
	[ACTIVITY_EVENT.NOTIFIED_POOL]: 'sky',
	[ACTIVITY_EVENT.NOTIFIED_RIDER]: 'sky',
	[ACTIVITY_EVENT.DELAY_CHANGED]: 'amber',
	[ACTIVITY_EVENT.EDITED]: 'amber',
	[ACTIVITY_EVENT.UNASSIGNED]: 'amber',
	[ACTIVITY_EVENT.REVOKED]: 'red',
	[ACTIVITY_EVENT.CANCELLED]: 'red',
	[ACTIVITY_EVENT.DELETED]: 'red',
	// [ACTIVITY_EVENT.SWEEP_PULLED]: 'sky',
	[ACTIVITY_EVENT.CLOCK_IN]: 'green',
	[ACTIVITY_EVENT.CLOCK_OUT]: 'slate',
	[ACTIVITY_EVENT.BREAK_IN]: 'slate',
	[ACTIVITY_EVENT.BREAK_OUT]: 'green',
};

export const TONE_CLASSES = {
	teal: { dot: 'bg-teal-500', text: 'text-teal-700' },
	violet: { dot: 'bg-violet-500', text: 'text-violet-700' },
	green: { dot: 'bg-emerald-500', text: 'text-emerald-700' },
	sky: { dot: 'bg-sky-500', text: 'text-sky-700' },
	amber: { dot: 'bg-amber-500', text: 'text-amber-700' },
	red: { dot: 'bg-red-500', text: 'text-red-700' },
	slate: { dot: 'bg-slate-400', text: 'text-slate-600' },
};

export const toneOf = type => TONES[type] || 'slate';

// An elapsed time in words, from a count of seconds.
//
// Seconds only below a minute. These gaps are usually small — a rider answers a
// push in eight seconds — but nothing bounds them: an order left on the board
// overnight produced «768258δ», which is nine days written as a number nobody
// can read. Each step keeps one unit of detail below the leading one, so «2ω
// 15λ» stays useful while «9ημ» stops pretending the minutes matter.
const duration = n => {
	if (n === null || n === undefined) return null;

	const total = Math.round(Number(n));
	if (!Number.isFinite(total) || total < 0) return null;

	if (total < 60) return `${total}δ`;

	const minutes = Math.round(total / 60);
	if (minutes < 60) return `${minutes}λ`;

	const hours = Math.floor(minutes / 60);
	const restMinutes = minutes % 60;
	if (hours < 24) return restMinutes ? `${hours}ω ${restMinutes}λ` : `${hours}ω`;

	const days = Math.floor(hours / 24);
	const restHours = hours % 24;
	return restHours ? `${days}ημ ${restHours}ω` : `${days}ημ`;
};

// The rider's CURRENT name where the page knows it, falling back to the one the
// log froze at the time. A renamed rider should not read as two people.
// Optional-chained throughout, like the rest of describeActivity: that function
// already tolerates a row with no type and no meta, and this was the one place
// that did not, so a null row threw instead of rendering «Άγνωστο συμβάν».
const riderName = (event, deliveryById) => {
	const live = deliveryById?.[event?.deliveryId];
	if (live?.firstname || live?.lastname) {
		return `${live.firstname || ''} ${live.lastname || ''}`.trim();
	}
	return event?.meta?.deliveryName || null;
};

// What made the check come early, for when the standalone row comes back. The
// backend still names the action on every request — only the log row is parked.
// const SWEEP_REASON = {
// 	'order-ready': 'επειδή μια παραγγελία έγινε έτοιμη',
// 	'order-created': 'επειδή καταχωρήθηκε παραγγελία',
// 	'order-completed': 'επειδή ένας διανομέας ελευθερώθηκε',
// 	'order-cancelled': 'επειδή ακυρώθηκε παραγγελία που είχε διανομέα',
// 	'order-revoked': 'επειδή επιστράφηκε παραγγελία που δεν απαντήθηκε',
// 	'admin-unassigned': 'επειδή ο υπεύθυνος αφαίρεσε διανομέα',
// 	'rider-clocked-in': 'επειδή ένας διανομέας ξεκίνησε βάρδια',
// 	'rider-back-from-break': 'επειδή ένας διανομέας γύρισε από διάλειμμα',
// };

// Whether this action is what brought the automatic check forward, and by how
// much. Rendered as a mark on the row rather than as words — see ActivityFeed —
// because it is a property OF the action, not another thing that happened, and
// spelling it out doubled the length of every clock-in line.
export const pulledSweepSeconds = event => Number(event?.meta?.pulledSweepBySeconds) || null;

const assignedByText = by => {
	if (by === 'auto') return 'αυτόματα';
	if (by === 'admin') return 'χειροκίνητα';
	if (by === 'self') return 'μόνος του';
	if (by === 'wolt') return 'στη Wolt Drive';
	return null;
};

// { title, detail } — the title is the sentence, the detail is the aside that
// explains a number in it. Never put in `detail` anything the title needs.
export const describeActivity = (event, { deliveryById } = {}) => {
	const meta = event?.meta || {};
	const who = riderName(event, deliveryById);
	const shop = meta.shopName ? ` · ${meta.shopName}` : '';

	switch (event?.type) {
		case ACTIVITY_EVENT.CREATED:
			return {
				// A confirmed draft and an order typed on the board arrive by the same
				// route and used to read identically. The distinction matters: one is
				// somebody deciding, the other is somebody typing.
				title: meta.fromDraft ? `Επιβεβαιώθηκε από πρόχειρη${shop}` : `Καταχωρήθηκε${shop}`,
				detail: [
					meta.platform ? `από ${meta.platform}` : null,
					// How long it sat as a draft — the wait nobody could see. Only ever
					// present on a confirmed draft.
					duration(meta.draftWaitedSeconds)
						? `περίμενε ${duration(meta.draftWaitedSeconds)} ως πρόχειρη`
						: null,
					meta.delayMinutes ? `ετοιμασία ${meta.delayMinutes}λ` : 'χωρίς χρόνο ετοιμασίας',
				]
					.filter(Boolean)
					.join(' · '),
			};

		case ACTIVITY_EVENT.READY:
			return {
				title: `Έτοιμη${shop}`,
				detail: meta.alreadyAssigned ? 'την είχε ήδη διανομέας' : '',
			};

		case ACTIVITY_EVENT.NOTIFIED_POOL: {
			const riders = Number(meta.riders) || 0;
			return {
				title: riders === 1 ? 'Ειδοποιήθηκε 1 διανομέας' : `Ειδοποιήθηκαν ${riders} διανομείς`,
				detail: 'είναι διαθέσιμη για αποδοχή',
			};
		}

		case ACTIVITY_EVENT.NOTIFIED_RIDER:
			return {
				title: who ? `Ειδοποίηση στον ${who}` : 'Ειδοποίηση στον διανομέα',
				detail: meta.attempt > 1 ? `${meta.attempt}η αποστολή — δεν έχει απαντήσει` : '',
			};

		case ACTIVITY_EVENT.ASSIGNED:
			return {
				title: who ? `Ανατέθηκε στον ${who}` : 'Ανατέθηκε',
				detail: [assignedByText(meta.assignedBy), meta.shopName].filter(Boolean).join(' · '),
			};

		case ACTIVITY_EVENT.CONFIRMED:
			return {
				title: who ? `${who}: είδε την παραγγελία` : 'Ο διανομέας είδε την παραγγελία',
				detail: duration(meta.secondsAfterAssign)
					? `${duration(meta.secondsAfterAssign)} μετά την ανάθεση`
					: '',
			};

		case ACTIVITY_EVENT.COLLECTED:
			return {
				title: who ? `${who}: παρέλαβε` : 'Παραλήφθηκε',
				detail: [
					meta.shopName,
					duration(meta.secondsAfterAssign)
						? `${duration(meta.secondsAfterAssign)} μετά την ανάθεση`
						: null,
				]
					.filter(Boolean)
					.join(' · '),
			};

		case ACTIVITY_EVENT.UNASSIGNED:
			return {
				title: who ? `Αφαιρέθηκε από τον ${who}` : 'Αφαιρέθηκε ο διανομέας',
				detail: meta.wasWoltDrive ? 'ήταν σε Wolt Drive' : 'από τον υπεύθυνο',
			};

		case ACTIVITY_EVENT.REVOKED:
			return {
				title: who
					? `Επιστράφηκε — δεν απάντησε ο ${who}`
					: 'Επιστράφηκε — δεν απάντησε ο διανομέας',
				detail: 'ξαναμπαίνει στη λίστα, χωρίς αυτόν',
			};

		case ACTIVITY_EVENT.COMPLETED:
			return {
				title: who ? `Ολοκληρώθηκε — ${who}` : 'Ολοκληρώθηκε',
				detail: duration(meta.secondsFromAssign)
					? `${duration(meta.secondsFromAssign)} από την ανάθεση`
					: '',
			};

		case ACTIVITY_EVENT.CANCELLED:
			return { title: `Ακυρώθηκε${shop}`, detail: who ? `την είχε ο ${who}` : '' };

		case ACTIVITY_EVENT.DELAY_CHANGED:
			return {
				title: `Άλλαξε ο χρόνος ετοιμασίας${shop}`,
				detail:
					meta.minutesFromNow === null || meta.minutesFromNow === undefined
						? ''
						: `έτοιμη σε ${meta.minutesFromNow}λ από τη στιγμή της αλλαγής`,
			};

		case ACTIVITY_EVENT.EDITED:
			return {
				title: `Τροποποιήθηκε${shop}`,
				detail: meta.fields?.length ? meta.fields.join(', ') : '',
			};

		case ACTIVITY_EVENT.DELETED:
			return {
				title: 'Διαγράφηκε οριστικά',
				detail: [meta.price ? `${meta.price}€` : null, 'δεν υπάρχει πια στο ιστορικό']
					.filter(Boolean)
					.join(' · '),
			};

		case ACTIVITY_EVENT.CLOCK_IN:
			return {
				title: who ? `${who}: ξεκίνησε βάρδια` : 'Έναρξη βάρδιας',
				detail: '',
			};
		case ACTIVITY_EVENT.CLOCK_OUT:
			return { title: who ? `${who}: τέλος βάρδιας` : 'Τέλος βάρδιας', detail: '' };
		case ACTIVITY_EVENT.BREAK_IN:
			return { title: who ? `${who}: διάλειμμα` : 'Διάλειμμα', detail: '' };
		case ACTIVITY_EVENT.BREAK_OUT:
			return {
				title: who ? `${who}: επέστρεψε από διάλειμμα` : 'Επιστροφή από διάλειμμα',
				detail: '',
			};

		default:
			// A type the backend added and the page has not learned yet. Shown as
			// itself rather than dropped — an unexplained row beats a missing one.
			return { title: event?.type || 'Άγνωστο συμβάν', detail: '' };
	}
};

// Who did it, when it is worth saying. The system doing something is the normal
// case and adding "(σύστημα)" to two thirds of the feed would be noise.
export const actorLabel = event => {
	if (event?.actorType === 'admin') return 'υπεύθυνος';
	if (event?.actorType === 'shop') return 'κατάστημα';
	return null;
};

export const formatClock = value => {
	const d = value instanceof Date ? value : new Date(value);
	if (Number.isNaN(d.getTime())) return '';
	// hour12 false explicitly: el-GR defaults to a 12-hour clock and appends
	// «μ.μ.», which wraps the time column onto two lines and means nothing on a
	// board people read at a glance.
	return d.toLocaleTimeString('el-GR', {
		hour: '2-digit',
		minute: '2-digit',
		second: '2-digit',
		hour12: false,
	});
};
